// Talent Management > Offers: every extracted offer, across every project.
//
// The per-project view of the same rows already exists (a project's Sellers
// tab); this is the cross-project lane an operator watches during a rollout:
// what came back today, from whom, for how much, and for which project. So the
// two links on a row are the whole point. The talent opens their page; the
// PROJECT opens the conversation the offer answers.
//
// Same lane and same rules as the Searches tab beside it: OPERATIONAL
// per-record truth from Postgres, newest first, keyset "load more". No rate, no
// average, no trend — those are metrics, and metrics live in the BI-backed
// Dashboard tab (lib/seller-threads.ts states the line in full).
import {
  Badge,
  Button,
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  facetOptions,
  isDateRangeActive,
  SortableTh,
  TableExport,
  TableFilters,
  type DateRange,
  type FilterColumn,
  useColumnFilters,
  useRestoredState,
  useTableSort,
} from "@recruiter/shared";
import type { AdminOfferRow } from "@recruiter/shared/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { AreaBoundary } from "../components/area-gate";
import { ArrowRightIcon } from "../components/icons";
import { SectionTabs, TALENT_TABS } from "../components/section-tabs";
import { EmptyState, TableSkeleton } from "../components/states";
import { ExplorerProjectLink, ExplorerTalentLink } from "../components/explorer-link";
import { formatDateTime, formatOfferPrice } from "../lib/format";
import { projectTitle } from "../lib/project-title";
import { talentOffersQuery } from "../lib/queries";
import "./searches.css";

/** JUNO's headline 0-100 fit verdict for the offer. Zero is a REAL score under
 *  the binary verdict regime, so it renders as a number, not a dash. */
function Fit({ value }: { value: number }) {
  return <span title="JUNO's fit score for this offer, 0-100">{value}</span>;
}

/** Sort accessors, per lib/table-sort.ts: value EXTRACTORS, so Project orders by
 *  the DERIVED title rather than the raw `projects.name` (routinely empty, which
 *  would bunch every unnamed project at one end). Price orders on cents across
 *  currencies, which is a known approximation and the reason the Currency filter
 *  beside it exists: narrow to one currency and the column is exact. */
const OFFER_SORT = {
  received: (o: AdminOfferRow) => o.receivedAt ?? null,
  talent: (o: AdminOfferRow) => o.sellerId,
  project: (o: AdminOfferRow) =>
    o.project.id
      ? projectTitle({ id: o.project.id, name: o.project.name, summary: o.project.summary })
      : null,
  price: (o: AdminOfferRow) => o.priceCents,
  delivery: (o: AdminOfferRow) => o.timelineDays,
  fit: (o: AdminOfferRow) => o.fitScore,
} as const;

/** Recommended is CLOSED, so "nothing is recommended yet" reads as an answer.
 *  Both filters travel to the SERVER, where they narrow the whole lane rather
 *  than the loaded pages. */
const OFFER_RECOMMENDED_OPTIONS = [
  { value: "yes", label: "Recommended" },
  { value: "no", label: "Not recommended" },
] as const;

function OfferRow({ offer }: { offer: AdminOfferRow }) {
  const project = offer.project;
  return (
    <tr>
      <td>{formatDateTime(offer.receivedAt ?? null)}</td>
      <td>
        <ExplorerTalentLink sellerId={offer.sellerId} className="ex-primary-link">
          {offer.sellerId}
        </ExplorerTalentLink>
        {offer.recommended ? (
          <Badge
            tone="success"
            size="sm"
            className="rc-count-badge"
            title="The run's current recommendation: STERLING's suggestion while the run is live, the client's own pick once it completes"
          >
            Recommended
          </Badge>
        ) : null}
      </td>
      <td>
        {project.id ? (
          <ExplorerProjectLink projectId={project.id} className="ex-primary-link">
            {projectTitle({ id: project.id, name: project.name, summary: project.summary })}
          </ExplorerProjectLink>
        ) : (
          // The project row is gone (deleted account); the offer record remains.
          <span className="tm-unknown">Project deleted</span>
        )}
        {project.owner ? <span className="rc-cell-sub">{project.owner}</span> : null}
      </td>
      <td className="rc-num">
        {formatOfferPrice(offer.priceCents, offer.currency, offer.priceMaxCents ?? null)}
      </td>
      <td className="rc-num" title="Delivery the talent quoted, in days">
        {offer.timelineDays}d
      </td>
      <td className="rc-num">
        <Fit value={offer.fitScore} />
      </td>
      <td>
        {project.id ? (
          <ExplorerProjectLink
            projectId={project.id}
            className="ex-row-link"
            ariaLabel="Open the project this offer answers"
            denied={null}
          >
            <ArrowRightIcon size={16} />
          </ExplorerProjectLink>
        ) : null}
      </td>
    </tr>
  );
}

export function TalentOffersPage() {
  const [recommended, setRecommended] = useRestoredState<readonly string[]>(
    "talent-offers/recommended",
    [],
  );
  const [currencies, setCurrencies] = useRestoredState<readonly string[]>(
    "talent-offers/currencies",
    [],
  );
  // The cohort range. Local state like the filters beside it, and it narrows
  // SERVER-side for the same reason they do: this list is keyset-paged, so a
  // browser-side date filter could only ever cover the pages already fetched.
  const [range, setRange] = useRestoredState<DateRange>("talent-offers/range", EMPTY_DATE_RANGE);
  const offers = useInfiniteQuery(talentOffersQuery({ recommended, currencies, range }));
  // Above the error branch: hooks cannot sit behind an early return.
  const loaded = useMemo<AdminOfferRow[]>(
    () => offers.data?.pages.flatMap((p) => p.offers) ?? [],
    [offers.data],
  );
  /** The two repeating columns on a row that is otherwise one talent, one
   *  project, one price and one date. "Only the recommended ones" is how this
   *  lane is read during a rollout, and currency is what makes the Price
   *  column comparable. Currency options come from the response's store-wide
   *  facet, so a currency the gateway starts returning shows up without a
   *  release, and the control disappears on the usual all-USD day. */
  const knownCurrencies = offers.data?.pages[0]?.currencies;
  const columns = useMemo<readonly FilterColumn<AdminOfferRow>[]>(
    () => [
      {
        key: "recommended",
        label: "Filter by recommendation",
        allLabel: "Recommended: any",
        value: (o) => (o.recommended ? "yes" : "no"),
        options: OFFER_RECOMMENDED_OPTIONS,
        selected: recommended,
        onChange: setRecommended,
      },
      {
        key: "currency",
        label: "Filter by currency",
        allLabel: "All currencies",
        value: (o) => o.currency,
        options: facetOptions(knownCurrencies ?? []),
        selected: currencies,
        onChange: setCurrencies,
      },
    ],
    [knownCurrencies, recommended, currencies, setRecommended, setCurrencies],
  );
  const filters = useColumnFilters(loaded, columns);
  const sorted = useTableSort(filters.rows, OFFER_SORT, null, { restore: "talent-offers/sort" });

  if (offers.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary
          area="talent_management"
          error={offers.error}
          onRetry={() => void offers.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Offers</h1>
        <p className="ad-page__sub">
          Every offer talent sent back, newest first. The project link opens the conversation the
          offer answers; the talent link opens their record.
        </p>
      </header>

      <SectionTabs label="Talent Management sections" tabs={TALENT_TABS} />

      <div className="rc-toolbar sr-toolbar">
        <DateRangeFilter label="Received" value={range} onChange={setRange} />
        <TableFilters
          controls={filters.controls}
          active={filters.active || isDateRangeActive(range)}
          onClear={() => {
            filters.clear();
            setRange(EMPTY_DATE_RANGE);
          }}
        />
        {/* Says "loaded", not "offers", for the same reason the Searches tab
            does: this list is keyset-paginated, so SORTING reaches the pages
            fetched so far and no further. The filters run server-side and
            cover the whole lane — every loaded row already matches them. */}
        {offers.data ? (
          <span className="rc-toolbar__count">
            {sorted.rows.length.toLocaleString()} of {loaded.length.toLocaleString()} loaded
            {offers.hasNextPage ? ", more available" : ""}
          </span>
        ) : null}
      </div>

      {offers.isPending ? (
        <TableSkeleton columns={6} />
      ) : loaded.length === 0 && !filters.active ? (
        <EmptyState
          title="No offers yet"
          message="When talent replies to an outreach with an offer, it will appear here."
        />
      ) : sorted.rows.length === 0 ? (
        <EmptyState
          title="No offers match"
          message="The whole lane was searched. Try clearing a filter."
        />
      ) : (
        <>
          <div className="rc-table-wrap">
            <table className="rc-table">
              <TableExport name="talent-offers" label="offers" />
              <thead>
                <tr>
                  <SortableTh
                    sortKey="received"
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                  >
                    Received
                  </SortableTh>
                  <SortableTh sortKey="talent" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Talent
                  </SortableTh>
                  <SortableTh sortKey="project" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Project
                  </SortableTh>
                  <SortableTh
                    sortKey="price"
                    numeric
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                  >
                    Price
                  </SortableTh>
                  <SortableTh
                    sortKey="delivery"
                    numeric
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                  >
                    Delivery
                  </SortableTh>
                  <SortableTh
                    sortKey="fit"
                    numeric
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                  >
                    Fit
                  </SortableTh>
                  <th>
                    <span className="ad-visually-hidden">Open project</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.rows.map((offer) => (
                  <OfferRow key={offer.id} offer={offer} />
                ))}
              </tbody>
            </table>
          </div>
          {offers.hasNextPage ? (
            <div className="ex-load-more">
              <Button
                type="button"
                variant="outline"
                tone="neutral"
                size="sm"
                disabled={offers.isFetchingNextPage}
                onClick={() => void offers.fetchNextPage()}
              >
                {offers.isFetchingNextPage ? "Loading…" : "Load more offers"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
